import { AxiosError } from 'axios'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { MenuItem, TextFieldProps, Typography } from '@mui/material'
import { LiveListEntity } from '@/types/live-lists'
import { ProjectGridField } from '@/types/fields'
import { useGetIntegrationLiveListType } from '@/service-library/hooks/integration-live-list-types'
import { decodeHtml } from '@/utils/text'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import useForeignKeyListEndpoint from '@/components/project-settings/params-schema-property-fields/useForeignKeyListEndpoint'
import BasePickerField from './BasePickerField'

type LiveListPickerFieldProps = {
  onChange: (
    newValue: string | null,
    refId: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jsonObject?: Record<string, any> | null,
  ) => void
  field: ProjectGridField
  value: LiveListEntity | null
  setDisableArrowKeys?: Dispatch<SetStateAction<boolean>>
  isInTable?: boolean
} & Omit<TextFieldProps, 'onChange' | 'value'>

export default function LiveListPickerField({
  field,
  value,
  label,
  onChange,
  setDisableArrowKeys = () => {},
  helperText,
  ...props
}: LiveListPickerFieldProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(
    decodeHtml(value?.pd_display_name) || '',
  )
  const [enabled, setEnabled] = useState(false)
  const [inputValue, setInputValue] = useState(
    decodeHtml(value?.pd_display_name) || '',
  )
  const [liveListHelperText, setLiveListHelperText] = useState<
    string | undefined
  >(undefined)

  const { integrationLiveListType } = useGetIntegrationLiveListType({
    id: field.params?.live_list_type_id || '',
  })
  const { document } = useDocumentContext()
  const { allData, hasNextPage, fetchNextPage, isPending, error, isSuccess } =
    useForeignKeyListEndpoint<LiveListEntity>({
      url: integrationLiveListType?.url,
      filters: {
        query: searchValue,
        org_id: document?.owner_org_id,
      },
      enabled: open,
      retry: (failureCount, error) => {
        const status = (error as AxiosError).response?.status
        if (status && [400, 422].includes(status)) return false
        return failureCount < 3
      },
    })

  useEffect(() => {
    if (error) {
      const { response } = error as AxiosError
      const status = response?.status
      const code = (response?.data as unknown as Record<string, string>)?.code
      if (status === 400) {
        setLiveListHelperText(
          'Unable to retrieve items. Please try again later.',
        )
      } else if (status === 422 && code === 'missing_credentials') {
        setLiveListHelperText(
          "Unable to retrieve items. Are there proper credentials for this document's organization?",
        )
      } else if (status === 422 && code === 'invalid_credentials') {
        setLiveListHelperText(
          "Unable to retrieve items. Are the credentials for this document's organization valid?",
        )
      }
    } else if (isSuccess) {
      setLiveListHelperText(undefined)
    }
  }, [error, isSuccess])

  return (
    <BasePickerField<LiveListEntity>
      value={value}
      label={label}
      enabled={enabled}
      setEnabled={setEnabled}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onChange={(newValue) => {
        setOpen(false)
        onChange(newValue?.pd_display_name || '', null, newValue)
      }}
      options={allData as LiveListEntity[]}
      open={open}
      setOpen={setOpen}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      getOptionLabel={(option) => decodeHtml(option.pd_display_name) || ''}
      isOptionEqualToValue={(option, value) =>
        option?.entity_id === value.entity_id
      }
      isLoading={isPending}
      renderOption={(props, option) => {
        return (
          <MenuItem
            {...props}
            key={props.id}
            value={option.entity_id}
            sx={{ alignItems: 'center' }}
          >
            {decodeHtml(option.pd_display_name)}
            {option.pd_display_detail && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, mt: '1px' }}
              >
                {option.pd_display_detail}
              </Typography>
            )}
          </MenuItem>
        )
      }}
      setDisableArrowKeys={setDisableArrowKeys}
      helperText={liveListHelperText || helperText}
      {...props}
    />
  )
}
